import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetStatsQuery } from '@platform/state/api';
import { useDesignCounts } from '@platform/features/modules/design/useDesignCounts';
import { useDesignSection } from '@platform/features/modules/design/useDesignSection';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { ModuleStringsProvider } from '@platform/modularity/ModuleStringsProvider';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { cx } from '@platform/core/lib/cx';
import { MODULE_RETURN, routes } from '@platform/core/lib/routes';
import { useModulePageMemory } from '@platform/engine/list/landing/useModulePageMemory';
import type { IDesignEntity } from '@platform/core/types';
import { useAppSelector } from '@platform/state/hooks';
import { selectUi } from '@platform/state/selectors';
import { ConfigMenuGroups } from './ConfigMenuGroups';
import { hasOwnMenu } from './customMenuHeads';
import { designEntityLabel } from './designEntityRules';
import type { IModuleMenuProps } from './IModuleMenuProps';
import { MenuHeader } from './MenuHeader';
import { MenuLinksGroup } from './MenuLinksGroup';
import { lawModelLinkOf } from './lawModelLink';
import { libraryViewOf } from './libraryModes';
import { childModuleType } from './childModuleType';
import { catalogueLinksOf, designLinksOf, pageLinksOf, settingsLinksOf } from './menuLinks';
import { MenuPages } from './MenuPages';
import { MenuRecordsPart } from './MenuRecordsPart';
import { menuViewsOf, moduleMenuContextOf, pagesForContext } from './moduleMenuContext';
import { mainRecordTypeOf, menuSectionsOf } from './moduleSections';
import { menuSessionSlot } from './menuSessionSlot';
import { useMenuOpenState } from './useMenuOpenState';
import './menu.css';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';

/**
 * Ліве меню модуля за прототипом v4 (UIUX layout/left-menu, module-menu): заголовок (категорія, назва, «N записів» поточної таблиці,
 * на сторінках модуля — головної), сторінки модуля з іконками (набір — за контекстом меню, pagesForContext), робота із записами,
 * «Налаштування» (три пункти й довідники модуля), «Проєктування». Робота із записами залежить від контексту (moduleMenuContextOf): у списках,
 * на сторінках модуля й запису — MenuRecordsPart (бібліотека, власне меню модуля, розділи типів чи групи головного типу, «Таблиці»); у
 * налаштуваннях і «Моделі даних» модулі з власним меню (завдання, база знань) лишають його, решта — загальні групи головного типу й «Таблиці»
 * (ConfigMenuGroups); у розділах-списках «Проєктування» — лише загальні групи, а в модулях із власним меню — нічого. Секції вмикає
 * definition.menuSections; розгорнутість розділів пам'ятається для кожного модуля, «Налаштування» й «Проєктування» — у пам'яті вкладки. Контейнер — класи оболонки .po-menu/.po-menu__nav (248px,
 * тло --side, прокрутка); згорнуте ліве меню (ui.leftMenuCollapsed) сховане.
 */
export function ModuleMenu({ ws, moduleKey, recordTypeKey }: IModuleMenuProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings(moduleKey);
  const { data: module } = useModuleDefinition(moduleKey);
  const location = useLocation();
  const { pathname } = location;
  const params = new URLSearchParams(location.search);
  // Перехід на корінь модуля — нове відкриття (openModule прототипу): «Налаштування», «Проєктування» й розділи типів — типово згорнуті;
  // повернення до модуля (routes.moduleReturn: крихта, «← Модуль», «Відкрити модуль» — page:'module' прототипу) і «Назад» браузера їх не
  // скидають — вибір живе в пам'яті вкладки (menuSessionSlot: «Налаштування» — на шаблон меню, як n5/n6/n7_settings прототипу).
  const opening = pathname === routes.module(moduleKey) && !params.has(MODULE_RETURN);
  const slotOf = useCallback((id: string) => menuSessionSlot(moduleKey, id), [moduleKey]);
  const openState = useMenuOpenState(moduleKey, slotOf, opening ? location.key : undefined);
  const route = useWorkspaceParams();
  const design = useDesignSection();
  const onSettings = pathname.startsWith(routes.moduleSettings(moduleKey));
  const context = moduleMenuContextOf(design, onSettings, route.id);
  const own = hasOwnMenu(moduleKey);
  const designBase = routes.moduleDesign(moduleKey);
  const onDesign = pathname === designBase || pathname.startsWith(`${designBase}/`);
  // Лічильники «Проєктування» — лише коли група розгорнута (на сторінках проєктування — завжди, поза ними — вибором користувача), щоб не
  // запитувати їх на кожній сторінці.
  const designCounts = useDesignCounts(ws, onDesign || openState.isOpen('design', false) ? module : undefined);
  const collapsed = useAppSelector((s) => selectUi(s).leftMenuCollapsed);
  const main = module ? mainRecordTypeOf(module) : undefined;
  useModulePageMemory(ws, moduleKey, main?.key);
  // Дочірня таблиця — окремий модуль прототипу («Видатки»): на її списку й записі (маршрут) і на її налаштуваннях (?type=) меню рахує її
  // записи, показує її групи в налаштуваннях і веде «Налаштування» до неї.
  const child = module ? childModuleType(module, onSettings ? params.get('type') ?? params.get('focus') : recordTypeKey) : undefined;
  const counted = child ?? module?.recordTypes.find((r) => r.key === recordTypeKey) ?? main;
  const { data: stats } = useGetStatsQuery({ ws, rt: counted?.key ?? '', by: 'status', params: {} }, { skip: !counted });
  const asideClass = cx('po-menu mnav', collapsed && 'is-collapsed');
  if (!module || !main) return <aside className={asideClass} aria-label={t('menu.nav')} />;
  const sections = menuSectionsOf(module);
  const name = strings.moduleName(module.module.name);
  const category = (strings.lang === 'uk' ? module.module.categoryUk : null) ?? module.module.category;
  const current = module.recordTypes.some((r) => r.key === recordTypeKey) ? recordTypeKey : undefined;
  const allPages = sections.pages
    ? pageLinksOf(moduleKey, module, strings, (k) => t(`modulePages.${k}`)).map((l) => lawModelLinkOf(l, moduleKey, main.key, route))
    : [];
  const library = libraryViewOf(main);
  const pages = pagesForContext(library ? allPages.filter((l) => l.key !== library) : allPages, context, own, menuViewsOf(main));
  const libraryLabel = allPages.find((l) => l.key === library)?.label ?? t('menu.library.home');
  const generic = !own && (context === 'config' || context === 'design');
  const entityLabel = (rtKey: string, i: number, e: IDesignEntity) => designEntityLabel(strings.lang, moduleKey, rtKey, i, e);
  const settings = sections.settings
    ? [...settingsLinksOf(moduleKey, (k) => t(`settings.${k}`), child?.key), ...catalogueLinksOf(moduleKey, module, strings, entityLabel)] : [];
  return (
    <ModuleStringsProvider strings={strings}>
      <aside className={asideClass} aria-label={t('menu.nav')}>
        <nav className="po-menu__nav mnav__nav" aria-label={name}>
          <MenuHeader eyebrow={category} title={name} meta={stats ? t('modules.records', { count: stats.total }) : ''} />
          <MenuPages links={pages} />
          {generic && (
            <ConfigMenuGroups ws={ws} moduleKey={moduleKey} module={module} main={child ?? main} moduleName={name} withTables={context === 'config'} openState={openState} />
          )}
          {!generic && context !== 'design' && (
            <MenuRecordsPart ws={ws} moduleKey={moduleKey} module={module} main={main} moduleName={name} current={current} libraryLabel={libraryLabel}
              openState={openState} />
          )}
          <MenuLinksGroup id="settings" title={t('menu.settings')} links={settings} base={routes.moduleSettings(moduleKey)} openState={openState} />
          <MenuLinksGroup id="design" title={t('menu.design')} links={designLinksOf(moduleKey, designCounts, (k) => t(`design.${k}`))}
            base={routes.moduleDesign(moduleKey)} openState={openState} openOnPage />
        </nav>
      </aside>
    </ModuleStringsProvider>
  );
}
